import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { previewPhoto, confirmUpload } from '../services/api';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './PhotoUploader.css';
import { convertHeicToJpeg, compressImageAdvanced } from '../utils/imageUtils';
import { 
  FaCloudUploadAlt, 
  FaUser,
  FaInfoCircle,
  FaArrowLeft,
  FaArrowRight,
  FaCrop,
  FaCheckCircle
} from 'react-icons/fa';


const PhotoUploader = forwardRef((props, ref) => {
  // Tab state management
  const [currentTab, setCurrentTab] = useState(0);
  const tabs = ['Upload', 'Crop & Validate', 'Preview', 'Complete'];
  
  // File and preview states
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null); // Store original file for re-cropping
  const [previewUrl, setPreviewUrl] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null); // Store cropped file for confirm upload
  const [processedImages, setProcessedImages] = useState({}); // Store preview images for display
  
  // Upload states
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  
  // Drag and drop states
  const [isDraggingOverWindow, setIsDraggingOverWindow] = useState(false);
  
  // Cropper states
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0
  });
  const imageRef = useRef(null);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage);
      }
    };
  }, [previewUrl, croppedImage]);

  // Navigation functions
  const goToNextTab = useCallback(() => {
    if (currentTab < tabs.length - 1) {
      setCurrentTab(currentTab + 1);
    }
  }, [currentTab, tabs.length]);

  // Go back to start for fresh upload
  const startFreshUpload = () => {
    // Clean up existing URLs
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (croppedImage) URL.revokeObjectURL(croppedImage);
    
    // Reset all states
    setSelectedFile(null);
    setOriginalFile(null);
    setPreviewUrl(null);
    setCroppedImage(null);
    setCroppedFile(null);
    setProcessedImages({});
    setError(null);
    setIsUploading(false);
    setStatusMessage('');
    setUploadComplete(false);
    setUploadFailed(false);
    setIsCropping(false);
    setIsCompressing(false);
    
    // Reset crop
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0
    });
    
    // Go to first tab
    setCurrentTab(0);
  };

  // Expose startFreshUpload to parent components via ref
  useImperativeHandle(ref, () => ({
    resetToStart: startFreshUpload
  }));

  // Go back to crop tab with original image
  const goBackToCrop = () => {
    if (!originalFile) {
      console.error('No original file available for re-cropping');
      return;
    }
    
    // Clean up cropped image URL (but keep the current preview URL for now)
    if (croppedImage) URL.revokeObjectURL(croppedImage);
    
    // Create a fresh preview URL from the original file
    const originalFileURL = URL.createObjectURL(originalFile);
    
    // Clean up the old preview URL if it's different
    if (previewUrl && previewUrl !== originalFileURL) {
      URL.revokeObjectURL(previewUrl);
    }
    
    // Reset to crop tab with original image
    setCurrentTab(1);
    
    // Restore original file and preview URL
    setSelectedFile(originalFile);
    setPreviewUrl(originalFileURL);
    setCroppedImage(null);
    setProcessedImages({});
    
    // Clear upload states
    setError(null);
    setIsUploading(false);
    setStatusMessage('');
    setUploadComplete(false);
    setUploadFailed(false);
    setIsCropping(false);
    setIsCompressing(false);
    
    // Reset crop to default
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0
    });
    
    console.log('Restored original image for re-cropping:', {
      fileName: originalFile.name,
      fileSize: originalFile.size,
      previewUrl: originalFileURL
    });
  };

  // Create cropped image and upload
  const createCroppedImage = async () => {
    try {
      setIsCropping(true);
      
      if (!imageRef.current || !crop.width || !crop.height) {
        setError('Please select a crop area');
        setIsCropping(false);
        return;
      }

      const image = imageRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        setError('Canvas not supported');
        setIsCropping(false);
        return;
      }

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      // Calculate pixel values based on crop unit
      let pixelCrop;
      if (crop.unit === '%') {
        // For percentage-based crops
        pixelCrop = {
          x: (crop.x / 100) * image.naturalWidth,
          y: (crop.y / 100) * image.naturalHeight,
          width: (crop.width / 100) * image.naturalWidth,
          height: (crop.height / 100) * image.naturalHeight
        };
      } else {
        // For pixel-based crops
        pixelCrop = {
          x: crop.x * scaleX,
          y: crop.y * scaleY,
          width: crop.width * scaleX,
          height: crop.height * scaleY
        };
      }

      // Set canvas size to the crop size
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      // Draw the cropped image
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      // Convert to blob and handle async operations
      // Use JPEG with compression to reduce file size
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.85); // 85% quality for good balance
      });

      if (!blob) {
        setError('Failed to create cropped image');
        setIsCropping(false);
        return;
      }
      
      const croppedUrl = URL.createObjectURL(blob);
      setCroppedImage(croppedUrl);
      
      // Create a new file from the cropped blob with JPEG format
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, '') + '_cropped.jpg';
      const croppedFileObj = new File([blob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      setSelectedFile(croppedFileObj);
      setCroppedFile(croppedFileObj); // Store for confirm upload
      
      console.log('Crop details:', {
        original: { name: selectedFile.name, size: selectedFile.size },
        cropped: { name: croppedFileObj.name, size: croppedFileObj.size },
        crop: pixelCrop,
        canvasSize: { width: canvas.width, height: canvas.height }
      });
      
      // Check file size before upload (4MB max)
      const maxSizeBytes = 4 * 1024 * 1024; // 4MB
      if (croppedFileObj.size > maxSizeBytes) {
        setError(`Cropped image is too large (${(croppedFileObj.size / 1024 / 1024).toFixed(2)}MB). Please crop a smaller area or use a lower quality image. Maximum size is 4MB.`);
        setIsCropping(false);
        return;
      }
      
      // Stay on same tab and trigger preview with the freshly cropped file
      setIsCropping(false);
      await triggerPreview(croppedFileObj);
      
    } catch (error) {
      console.error('Error creating cropped image:', error);
      setError('Failed to crop image');
      setIsCropping(false);
    }
  };

  // Handle final upload confirmation to S3
  const handleConfirmUpload = async () => {
    try {
      if (!croppedFile) {
        setError('No cropped image available to upload');
        return;
      }
      
      setError(null);
      setUploadFailed(false);
      setIsUploading(true);
      setStatusMessage('Uploading to cloud storage...');
      
      // Upload the cropped file (optimized version - backend will process all platforms)
      const uploadResult = await confirmUpload(croppedFile);
      
      if (uploadResult && uploadResult.status === 'success') {
        setUploadComplete(true);
        setStatusMessage(uploadResult.message);
        // Move to complete tab
        setCurrentTab(3);
      } else if (uploadResult && uploadResult.status === 'partial') {
        setError(uploadResult.message);
        setUploadFailed(true);
      } else {
        throw new Error('Upload failed');
      }
      
    } catch (error) {
      // Handle validation errors from backend
      if (error.validationErrors) {
        // If we have validation errors array, use it
        setError(error.validationErrors);
      } else if (error.detail) {
        // If we have detail but it's not an array, use it as is
        setError(error.detail);
      } else {
        // Fall back to error message
        setError(error.message || 'Upload failed');
      }
      setUploadFailed(true);
    } finally {
      setIsUploading(false);
    }
  };

  // Preview and validate with API call (no S3 upload)
  const triggerPreview = async (fileOverride) => {
    try {
      const fileToUpload = fileOverride || selectedFile;
      if (!fileToUpload) {
        setError('No file selected for upload');
        return;
      }
      
      // Final file size check before upload (4MB max)
      const maxSizeBytes = 4 * 1024 * 1024; // 4MB
      if (fileToUpload.size > maxSizeBytes) {
        setError(`File is too large (${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB). Maximum allowed size is 4MB. Please crop a smaller area or use a smaller image.`);
        setUploadFailed(true);
        return;
      }
      
      setError(null);
      setUploadFailed(false);
      setIsUploading(true);
      setStatusMessage('Processing your photo for preview...');
      
      console.log('Processing file for preview:', {
        name: fileToUpload.name,
        size: fileToUpload.size,
        type: fileToUpload.type
      });
      
      // Create form data
      const formData = new FormData();
      formData.append('photo', fileToUpload);
      
      // Get preview without uploading to S3 (optimized - only returns Zoom image)
      const previewResult = await previewPhoto(formData);
      
      if (previewResult && previewResult.status === 'success') {
        // Store the Zoom preview for display in all platform previews
        const zoomPreviewUrl = `data:image/png;base64,${previewResult.preview_image}`;
        setProcessedImages({ zoom: zoomPreviewUrl });
        
        setUploadComplete(false); // Not uploaded yet, just previewed
        setStatusMessage('Photo validated and preview generated successfully');
        
        // Move to preview tab
        setCurrentTab(2);
      } else {
        throw new Error('Failed to generate preview');
      }
      
    } catch (error) {
      // Handle validation errors from backend
      if (error.validationErrors) {
        // If we have validation errors array, use it
        setError(error.validationErrors);
      } else if (error.detail) {
        // If we have detail but it's not an array, use it as is
        setError(error.detail);
      } else {
        // Fall back to error message
        setError(error.message || 'Preview failed');
      }
      setUploadFailed(true);
      setCurrentTab(1); // Stay on crop & process tab to show error
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError(null);
    setStatusMessage('');
    setUploadComplete(false);
    setUploadFailed(false);
    
    // Clean up previous preview URLs
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (croppedImage) {
      URL.revokeObjectURL(croppedImage);
    }
    
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      setError('Unsupported filetype: accepts only .jpg, .jpeg, .png and .heic');
      return;
    }
    
    if (acceptedFiles.length === 0) {
      return;
    }
    
    const file = acceptedFiles[0];
    
    // Check file type by extension
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.heic'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Unsupported filetype: accepts only .jpg, .jpeg, .png and .heic');
      return;
    }
    
    // Check initial file size (allow up to 100MB initially - will be compressed)
    const maxInitialSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxInitialSize) {
      setError(`Image is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Please use an image smaller than 100MB.`);
      return;
    }
    
    try {
      // Convert HEIC to JPEG if needed
      const heicProcessedFile = await convertHeicToJpeg(file);
      
      console.log('HEIC processing complete:', {
        original: file.name,
        processed: heicProcessedFile.name,
        type: heicProcessedFile.type,
        size: heicProcessedFile.size
      });
      
      // Check image dimensions
      const checkDimensions = (file) => {
        return new Promise((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ width: 0, height: 0 });
          };
          img.src = url;
        });
      };
      
      const dimensions = await checkDimensions(heicProcessedFile);
      console.log('Image dimensions:', dimensions);
      
      const compressionThreshold = 5 * 1024 * 1024; // 5MB threshold
      const maxDimension = 2000; // Max width or height in pixels
      let finalFile = heicProcessedFile;
      
      // Determine if we need to compress/resize
      const needsSizeCompression = heicProcessedFile.size > compressionThreshold;
      const needsDimensionResize = dimensions.width > maxDimension || dimensions.height > maxDimension;
      
      if (needsSizeCompression || needsDimensionResize) {
        // File needs compression/resizing
        setIsCompressing(true);
        
        if (needsSizeCompression && needsDimensionResize) {
          setStatusMessage('Compressing and resizing image...');
          console.log(`File size (${(heicProcessedFile.size / 1024 / 1024).toFixed(2)}MB) > 5MB AND dimensions (${dimensions.width}x${dimensions.height}) > 2000px. Compressing and resizing...`);
        } else if (needsSizeCompression) {
          setStatusMessage('Compressing image...');
          console.log(`File size (${(heicProcessedFile.size / 1024 / 1024).toFixed(2)}MB) exceeds 5MB threshold. Starting compression...`);
        } else {
          setStatusMessage('Resizing image...');
          console.log(`Image dimensions (${dimensions.width}x${dimensions.height}) exceed 2000px. Resizing...`);
        }
        
        const compressedFile = await compressImageAdvanced(heicProcessedFile, {
          maxSizeMB: 4, // Target max 4MB (backend requirement)
          maxWidthOrHeight: maxDimension, // Max dimension 2000px
          quality: 0.85 // 85% quality
        });
        
        setIsCompressing(false);
        setStatusMessage('');
        
        const newDimensions = await checkDimensions(compressedFile);
        
        console.log('Compression/resize complete:', {
          original: heicProcessedFile.name,
          compressed: compressedFile.name,
          originalSize: `${(heicProcessedFile.size / 1024 / 1024).toFixed(2)}MB`,
          compressedSize: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
          originalDimensions: `${dimensions.width}x${dimensions.height}`,
          newDimensions: `${newDimensions.width}x${newDimensions.height}`,
          reduction: `${(((heicProcessedFile.size - compressedFile.size) / heicProcessedFile.size) * 100).toFixed(1)}%`
        });
        
        // Check compressed file size (4MB max after compression)
        const maxSizeBytes = 4 * 1024 * 1024; // 4MB
        if (compressedFile.size > maxSizeBytes) {
          setError(`Image is still too large after compression (${(compressedFile.size / 1024 / 1024).toFixed(2)}MB). Please use a smaller image or reduce the resolution. Maximum size is 4MB.`);
          return;
        }
        
        finalFile = compressedFile;
      } else {
        // File is already under 5MB and dimensions are good, skip compression
        console.log(`File size (${(heicProcessedFile.size / 1024 / 1024).toFixed(2)}MB) is under 5MB and dimensions (${dimensions.width}x${dimensions.height}) are under 2000px. Skipping compression.`);
      }
      
      // Create preview URL
      const fileURL = URL.createObjectURL(finalFile);
      setPreviewUrl(fileURL);
      setSelectedFile(finalFile);
      setOriginalFile(finalFile); // Store file for potential re-cropping
    } catch (error) {
      console.error('Error processing file:', error);
      setError(error.message || 'Failed to process image');
      setIsCompressing(false);
      setStatusMessage('');
      return;
    }
    
    // Reset crop to full image size
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0
    });
    
    // Auto move to crop tab
    goToNextTab();
  }, [previewUrl, croppedImage, goToNextTab]);

  // Window-level drag and drop functionality
  useEffect(() => {
    // Only enable window drag/drop in upload picture tab
    const shouldEnableWindowDrop = currentTab === 0 && !uploadComplete && !isUploading && !isCompressing;
    
    if (!shouldEnableWindowDrop) return;

    let dragCounter = 0;

    const handleWindowDragEnter = (e) => {
      e.preventDefault();
      dragCounter++;
      if (dragCounter === 1) {
        setIsDraggingOverWindow(true);
      }
    };

    const handleWindowDragLeave = (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        setIsDraggingOverWindow(false);
      }
    };

    const handleWindowDragOver = (e) => {
      e.preventDefault();
    };

    const handleWindowDrop = (e) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDraggingOverWindow(false);
      
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onDrop(files, []);
      }
    };

    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [currentTab, uploadComplete, isUploading, isCompressing, onDrop]);
  
  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic'],
      'image/heif': ['.heic']
    },
    maxFiles: 1,
    disabled: isUploading || uploadComplete || isCompressing || currentTab !== 0
  });
  
  const onImageLoad = useCallback((e) => {
    console.log('Image loaded:', e.currentTarget.naturalWidth, 'x', e.currentTarget.naturalHeight);
  }, []);

  const guidelineSections = [
    {
      title: "What you'll need",
      items: [
        "A recent photo of you, facing the camera",
        "Light, simple background",
        "Good lighting",
      ]
    },
    {
      title: "Your photo should",
      items: [
        "Look professional and friendly",
        "Show your full face and shoulders, centered",
        "Be clear and in focus",
        "Include only you",
        "Be a JPG, JPEG, PNG, or HEIC file",
      ]
    },
    {
      title: "Please avoid",
      items: [
        "Sunglasses or anything that covers your eyes",
        "Side profiles or full-body shots",
        "Busy or cluttered backgrounds",
        "Group shots, event candids, or vacation photos",
      ]
    },
    {
      title: "We'll take care of the rest",
      items: [
        "We auto-resize and compress your photo",
        "We apply an EAB blue background for consistency",
        "You'll preview before you save",
      ]
    },
  ];
  
  return (
      <>
        {/* Header Description - Outside the main box */}
        <div className="px-4 md:px-6 py-3 md:py-4">
          <p className="text-gray-700 text-sm md:text-base">
            <span className="font-bold">Set your EAB profile photo.</span> A single upload updates your photo in Slack, Zoom, Salesforce, and Office 365. You can update your photo here any time.
          </p>
        </div>

      <div className="bg-white shadow-md">
        {/* Window Drag Overlay */}
      {isDraggingOverWindow && (
        <div className="fixed inset-0 bg-eab-blue bg-opacity-90 border-8 border-dashed border-white z-50 flex items-center justify-center">
          <div className="text-center text-white">
            <FaCloudUploadAlt className="text-white text-6xl mb-4 mx-auto animate-bounce" />
            <h3 className="text-2xl font-semibold mb-2">Drop your photo here</h3>
            <p className="text-lg opacity-90">Release to upload your profile photo</p>
          </div>
        </div>
      )}

                {/* Progress Steps */}
          <div className="bg-eab-light-blue px-4 md:px-6 py-4">
            <div className="max-w-5xl mx-auto">
              {/* Desktop Progress Steps */}
              <div className="hidden md:flex items-center justify-between">
                {tabs.map((tab, index) => (
                  <React.Fragment key={index}>
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 text-white ${
                        currentTab >= index ? 'bg-eab-blue' : 'bg-gray-400'
                      }`}>
                        {index + 1}
                      </div>
                      <span className={`font-medium ${currentTab >= index ? 'text-gray-800' : 'text-gray-400'}`} style={{ fontSize: '14px', marginLeft: '8px' }}>
                        {tab}
                      </span>
                    </div>
                    {index < tabs.length - 1 && (
                      <div className="flex-1 mx-4 border-t-2 border-dashed" style={{ borderColor: 'rgb(156, 163, 175)' }}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Mobile Progress - Only show current tab */}
              <div className="md:hidden text-center">
                <div className="flex items-center justify-center">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-eab-blue text-white text-xs font-bold">
                    {currentTab + 1}
                  </div>
                  <span className="font-medium text-gray-800" style={{ fontSize: '14px', marginLeft: '8px' }}>
                    {tabs[currentTab]}
                  </span>
                </div>
              </div>
            </div>
          </div>

              {/* Tab Content */}
        <div className="p-4 md:px-6 md:py-5">
          {/* Tab 1: Upload Picture */}
          {currentTab === 0 && (
          <div className="flex flex-col md:flex-row gap-6 md:gap-12">
            {/* Left Column - Guidelines */}
            <div className="order-2 md:order-1 w-full md:w-1/2 md:pr-12 md:border-r md:border-gray-200">
              <h3 className="text-base md:text-xl font-semibold text-gray-800 mb-2">Photo Guidelines</h3>
              <div className="space-y-4 md:space-y-4">
                {guidelineSections.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    <h4 className="text-sm md:text-base font-semibold text-gray-800 mb-2">{section.title}</h4>
                    <div className="space-y-1.5 md:space-y-2" style={{ marginLeft: '24px' }}>
                      {section.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start">
                          <span className="text-gray-500 mr-2 md:mr-3">•</span>
                          <p className="text-gray-700 text-sm md:text-base leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Upload Area */}
            <div className="flex flex-col items-center order-1 md:order-2 w-full md:w-1/2 md:pl-12" style={{marginTop: '20px'}}>
              <div className="w-32 h-32 md:w-64 md:h-64 border-2 border-dashed border-eab-blue flex items-center justify-center mb-4 md:mb-8 overflow-hidden" style={{background: 'linear-gradient(135deg, #BEE2F0 0%, #C6E3EF -66%, #DCEFF5 46%)'}}>
            {previewUrl ? (
                  <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" />
                ) : (
                  <div className="flex flex-col items-center">
                                         <FaUser className="text-eab-blue text-5xl md:text-5xl mb-2 md:mb-4" />
                     <p className="text-xs md:text-sm text-gray-500 text-center">Preview will appear here</p>
              </div>
            )}
          </div>

              {error && (
                <div className="w-full mb-3 md:mb-4 rounded-md md:rounded-lg p-2 md:p-3 bg-red-50 border border-red-200">
                  <div className="flex items-start">
                    <FaInfoCircle className="text-red-500 mt-0.5 md:mt-1 mr-2 text-xs md:text-sm" />
                    <div className="text-xs md:text-sm text-red-700">
                      {Array.isArray(error) ? (
                        <ul className="space-y-1">
                          {error.map((errorMsg, index) => (
                            <li key={index}>{errorMsg}</li>
                          ))}
                        </ul>
                      ) : error}
                    </div>
              </div>
            </div>
          )}

          <div className="w-full">
            <div
              {...getRootProps()}
                  className={`w-full mb-2 md:mb-4 ${isCompressing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${
                    isDragActive ? 'bg-eab-blue' : 'bg-eab-blue hover:bg-eab-blue'
                  } text-white font-medium py-3 md:py-4 px-4 rounded-md md:rounded-lg flex items-center justify-center transition-colors text-sm md:text-lg`}
            >
              <input {...getInputProps()} />
              {isCompressing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Compressing...
                </>
              ) : (
                <>
                  <FaCloudUploadAlt className="mr-2" />
                  {isDragActive ? 'Drop the image here' : 'Upload Photo'}
                </>
              )}
            </div>
            
            {isCompressing && statusMessage && (
              <div className="w-full mb-4 rounded-md p-3 bg-eab-light-blue">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-eab-blue mr-3"></div>
                  <p className="text-sm text-gray-700">{statusMessage}</p>
                </div>
              </div>
            )}
          </div>

          {/* Destination Platforms */}
              <div className="mt-6 md:mt-12 w-full" style={{marginTop: '30px'}}>
                <p className="text-xs md:text-base text-gray-600 mb-3 md:mb-6 text-center">Your photo will be uploaded to:</p>
                                <div className="grid grid-cols-4 gap-2 md:gap-6">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center" style={{ height: '50px', width: '50px' }}>
                      <img src="/Slack.svg" alt="Slack" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
                    </div>
                    <span className="text-xs md:text-sm mt-1 text-center">Slack</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center" style={{ height: '50px', width: '50px' }}>
                      <img src="/Zoom.svg" alt="Zoom" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                    </div>
                    <span className="text-xs md:text-sm mt-1 text-center">Zoom</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center" style={{ height: '50px', width: '50px' }}>
                      <img src="/Salesforce.svg" alt="Salesforce" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    </div>
                    <span className="text-xs md:text-sm mt-1 text-center">Salesforce</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center" style={{ height: '50px', width: '50px' }}>
                      <img src="/Office 365.svg" alt="Office 365" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
                    </div>
                    <span className="text-xs md:text-sm mt-1 text-center">Office 365</span>
                  </div>
            </div>
          </div>
        </div>
      </div>
        )}

        {/* Tab 2: Crop & Process */}
        {currentTab === 1 && previewUrl && (
          <div>
            {!isCropping && !isUploading && !uploadComplete && !uploadFailed && (
              <>
                <h3 className="text-base md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 text-center">
                  Crop Your Photo
                </h3>
                <div className="flex justify-center">
                  <div className="relative max-w-full md:max-w-2xl">
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => {
                        console.log('Crop changed:', c);
                        setCrop(c);
                      }}
                      onComplete={(c) => {
                        console.log('Crop complete:', c);
                        setCrop(c);
                      }}
                      keepSelection
                      minWidth={50}
                      minHeight={50}
                      className="max-h-64 md:max-h-96"
                      ruleOfThirds
                    >
                      <img
                        ref={imageRef}
                        src={previewUrl}
                        onLoad={onImageLoad}
                        alt="Crop preview"
                        style={{ maxHeight: '400px', maxWidth: '100%', display: 'block' }}
                      />
                    </ReactCrop>
                  </div>
                </div>
                <p className="text-center text-xs md:text-sm text-gray-600 mt-3">
                  Drag to adjust the crop area or leave as-is to upload the full image
                </p>
              </>
            )}

            {(isCropping || isUploading || uploadFailed) && (
              <div className="flex flex-col items-center justify-center py-4 md:py-8">
                  <img 
                    src={croppedImage || previewUrl} 
                    className="object-cover py-4"
                    style={{width: '30%', height: '30%'}}
                    alt="Cropped"
                    onError={(e) => {
                      console.error('Failed to load cropped image');
                      e.target.src = previewUrl; // Fallback to original
                    }}
                  />

                {!statusMessage && !error && (
                  <p className="text-sm md:text-base text-gray-600 mb-4">
                    {isCropping ? 'Cropping your photo...' : isUploading ? 'Uploading your photo...' : 'Processing your photo...'}
                  </p>
                )}

                {(statusMessage || error) && (
                  <div className="w-full max-w-md mb-4 md:mb-6">
                    <div className={`rounded-md md:rounded-lg p-3 md:p-4 ${error ? 'bg-red-50 border border-red-200' : 'bg-eab-light-blue'}`}>
                      <div className="flex items-start">
                        <FaInfoCircle className={`mt-0.5 md:mt-1 mr-2 text-xs md:text-sm ${error ? 'text-red-500' : 'text-eab-blue'}`} />
                        <div className="flex-1">
                          {error ? (
                            <div className="text-xs md:text-sm text-red-700" style={{ marginLeft: '15px' }}>
                              {Array.isArray(error) ? (
                                <ul className="space-y-1">
                                  {error.map((errorMsg, index) => (
                                    <li key={index}>{errorMsg}</li>
                                  ))}
                                </ul>
                              ) : error}
                            </div>
                          ) : (
                            <p className="text-xs md:text-sm text-gray-700">{statusMessage}</p>
                          )}
                          {!error && (isCropping || isUploading) && (
                            <div className="mt-2 md:mt-3 h-1.5 md:h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-eab-blue rounded-full animate-pulse" style={{ width: '100%' }}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Preview */}
        {currentTab === 2 && (
          <div>
            <h3 className="text-base md:text-xl font-semibold text-gray-800 mb-6 md:mb-8 text-center">Preview Your Photos</h3>
            
            {/* Show success message if upload complete */}
            {uploadComplete && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <FaCheckCircle className="text-green-500 mr-2" />
                  <span className="text-sm text-green-700">Images uploaded successfully! You will be redirected to complete screen.</span>
                </div>
              </div>
            )}
            
            {/* Show error if upload failed */}
            {uploadFailed && error && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-start bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <FaInfoCircle className="text-red-500 mr-2 mt-0.5" />
                  <div className="text-sm text-red-700">
                    {Array.isArray(error) ? (
                      <ul className="text-left">
                        {error.map((errorMsg, index) => (
                          <li key={index}>{errorMsg}</li>
                        ))}
                      </ul>
                    ) : error}
                  </div>
                </div>
              </div>
            )}
            
            <div className="preview-grid">
              {/* Slack - Rounded corners */}
              <div style={{ textAlign: 'center' }}>
                <div className="preview-image-container w-[200px] h-[200px] md:w-[280px] md:h-[280px] lg:w-[320px] lg:h-[320px]" style={{
                  margin: '0 auto 8px auto',
                  border: '2px solid #0069BF',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #BEE2F0 0%, #C6E3EF -66%, #DCEFF5 46%)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}>
                  {processedImages.zoom ? (
                    <img 
                      src={processedImages.zoom} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      alt="Slack"
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-eab-blue"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center" style={{ height: '32px', marginBottom: '4px' }}>
                  <img src="/Slack.svg" alt="Slack" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
                </div>
                <p className="text-[8px] text-gray-500">512 × 512px</p>
              </div>

              {/* Zoom - Rectangular */}
              <div style={{ textAlign: 'center' }}>
                <div className="preview-image-container w-[200px] h-[200px] md:w-[280px] md:h-[280px] lg:w-[320px] lg:h-[320px]" style={{
                  margin: '0 auto 8px auto',
                  border: '2px solid #0069BF',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #BEE2F0 0%, #C6E3EF -66%, #DCEFF5 46%)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}>
                  {processedImages.zoom ? (
                    <img 
                      src={processedImages.zoom} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      alt="Zoom"
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-eab-blue"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center" style={{ height: '32px', marginBottom: '4px' }}>
                  <img src="/Zoom.svg" alt="Zoom" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                </div>
                <p className="text-[8px] text-gray-500">400 × 400px</p>
              </div>
              
              {/* Salesforce - Round circle */}
              <div style={{ textAlign: 'center' }}>
                <div className="preview-image-container w-[200px] h-[200px] md:w-[280px] md:h-[280px] lg:w-[320px] lg:h-[320px]" style={{
                  margin: '0 auto 8px auto',
                  border: '2px solid #0069BF',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #BEE2F0 0%, #C6E3EF -66%, #DCEFF5 46%)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}>
                  {processedImages.zoom ? (
                    <img 
                      src={processedImages.zoom} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      alt="Salesforce"
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-eab-blue"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center" style={{ height: '32px', marginBottom: '4px' }}>
                  <img src="/Salesforce.svg" alt="Salesforce" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                </div>
                <p className="text-[8px] text-gray-500">300 × 200px</p>
              </div>
              
              {/* Office 365 - Round circle */}
              <div style={{ textAlign: 'center' }}>
                <div className="preview-image-container w-[200px] h-[200px] md:w-[280px] md:h-[280px] lg:w-[320px] lg:h-[320px]" style={{
                  margin: '0 auto 8px auto',
                  border: '2px solid #0069BF',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #BEE2F0 0%, #C6E3EF -66%, #DCEFF5 46%)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center'
                }}>
                  {processedImages.zoom ? (
                    <img 
                      src={processedImages.zoom} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      alt="AD"
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-eab-blue"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center" style={{ height: '32px', marginBottom: '4px' }}>
                  <img src="/Office 365.svg" alt="Office 365" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
                </div>
                <p className="text-[8px] text-gray-500">450 × 450px</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Complete */}
        {currentTab === 3 && (
          <div className="text-center py-8 md:py-12">
            <FaCheckCircle className="text-green-500 text-5xl md:text-6xl mx-auto mb-3 md:mb-4" />
            <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2 md:mb-3">Upload Complete!</h3>
            <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">Success! Your photo will update across Slack, Salesforce, Zoom, and Office 365 within 24 hours.</p>
          </div>
        )}
      </div>

              {/* Navigation Buttons */}
        {(currentTab === 1 || currentTab === 2) && (
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-gray-50">
        {currentTab === 1 && !isCropping && !isUploading && !uploadComplete && (
          <div className="flex justify-between w-full mt-2">
            <button 
              onClick={uploadFailed ? goBackToCrop : startFreshUpload}
              className="bg-eab-blue hover:bg-eab-blue text-white font-medium py-2 px-4 rounded-lg flex items-center text-sm md:text-base"
            >
              <FaArrowLeft className="mr-2" />
              Previous
            </button>
            {!error && !uploadFailed && (
              <button 
                onClick={createCroppedImage}
                disabled={isCropping}
                className={`${isCropping ? 'bg-gray-400 cursor-not-allowed' : 'bg-eab-blue hover:bg-eab-blue'} text-white font-medium py-2 px-4 rounded-lg flex items-center text-base`}
              >
                <FaCrop className="mr-2" />
                {isCropping ? 'Processing...' : 'Crop'}
              </button>
            )}
          </div>
        )}

        {currentTab === 2 && (
          <div className="flex justify-between w-full mt-2">
            <button 
              onClick={goBackToCrop}
              disabled={isUploading}
              className={`${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-eab-blue hover:bg-eab-blue'} text-white font-medium py-2 px-4 rounded-lg flex items-center text-sm md:text-base`}
            >
              <FaArrowLeft className="mr-2" />
              Previous
            </button>
            <button 
              onClick={handleConfirmUpload}
              disabled={isUploading || uploadComplete}
              className={`${isUploading || uploadComplete ? 'bg-gray-400 cursor-not-allowed' : 'bg-eab-blue hover:bg-eab-blue'} text-white font-medium py-2 px-4 rounded-lg flex items-center text-sm md:text-base`}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : uploadComplete ? (
                <>
                  <FaCheckCircle className="mr-2" />
                  Uploaded
                </>
              ) : (
                <>
                  Submit &nbsp;
                  <FaArrowRight className="ml-2" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
        )}
    </div>
      </>
  );
});

export default PhotoUploader; 